import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { UserPlus, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function formatError(detail) {
  if (!detail) return "Registrierung fehlgeschlagen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [riderName, setRiderName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password || !riderName.trim()) {
      toast.error("Bitte alle Felder ausfüllen.");
      return;
    }
    if (password.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen haben.");
      return;
    }
    setLoading(true);
    try {
      await register(email.trim(), password, riderName.trim());
      toast.success("Onboarding abgeschlossen. Hammerl ist bereit für dich.");
      navigate("/play", { replace: true });
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-hammerl-paper text-hammerl-ink">
      <header className="border-b-2 border-hammerl-ink bg-hammerl-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/brand/hammerl-logo.jpg" alt="Hobby Horsing Hammerl Logo" className="w-10 h-10 object-cover border border-hammerl-ink" />
            <div>
              <div className="font-bold text-sm tracking-tight">HOBBY HORSING HAMMERL GMBH &amp; CO. KG</div>
              <div className="text-xs text-hammerl-pencil font-mono">Onboarding-Portal V.7.3.1</div>
            </div>
          </Link>
        </div>
      </header>

      <main className="max-w-md mx-auto px-6 py-14">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-xs font-mono uppercase tracking-widest text-hammerl-pencil mb-2">Formular A-23 / Reiter-Stammdaten</div>
          <h1 className="font-bold text-4xl sm:text-5xl tracking-tighter leading-tight">
            Werde <span className="bg-hammerl-highlight px-2 -rotate-1 inline-block text-hammerl-paper">Hammerl-Reiter</span>.
          </h1>
          <p className="mt-3 text-sm text-hammerl-pencil">
            Ein Account = ein Reiter = 10 Entscheidungen pro Tag. So bleibst du fokussiert und unsere Stockpferde gesund.
          </p>
        </motion.div>

        <form onSubmit={handleSubmit} className="mt-8 bg-hammerl-card border-2 border-hammerl-ink shadow-brutal p-6 space-y-4" data-testid="register-form">
          <div>
            <label className="font-bold text-sm">Reiter-Pseudonym</label>
            <Input
              value={riderName}
              onChange={(e) => setRiderName(e.target.value)}
              placeholder="z.B. Hansi Galoppmeister"
              maxLength={48}
              className="mt-1 rounded-none border-0 border-b-2 border-hammerl-ink bg-transparent focus-visible:ring-0 focus-visible:border-hammerl-corpblue px-1"
              data-testid="register-name-input"
            />
            <div className="text-[10px] font-mono text-hammerl-pencil mt-1">Erscheint im Spiel und im Hammerl Charts Leaderboard.</div>
          </div>
          <div>
            <label className="font-bold text-sm">E-Mail</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="reiter@galopp.de"
              autoComplete="email"
              className="mt-1 rounded-none border-0 border-b-2 border-hammerl-ink bg-transparent focus-visible:ring-0 focus-visible:border-hammerl-corpblue px-1"
              data-testid="register-email-input"
            />
          </div>
          <div>
            <label className="font-bold text-sm">Passwort <span className="text-hammerl-pencil text-xs font-normal">(min. 6 Zeichen)</span></label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="••••••••"
              className="mt-1 rounded-none border-0 border-b-2 border-hammerl-ink bg-transparent focus-visible:ring-0 focus-visible:border-hammerl-corpblue px-1"
              data-testid="register-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full rounded-none bg-hammerl-corpblue hover:bg-hammerl-ink text-white font-bold shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all"
            data-testid="register-submit-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" /> {loading ? "Onboarding läuft..." : "Karriere starten"}
          </Button>
          <div className="text-center text-sm">
            <span className="text-hammerl-pencil">Schon registriert? </span>
            <Link to="/login" className="font-bold underline decoration-hammerl-corpblue decoration-2 underline-offset-2" data-testid="goto-login-link">
              Anmelden
            </Link>
          </div>
          <div className="text-[10px] font-mono text-hammerl-pencil text-center pt-2">
            * Mit der Anmeldung akzeptierst du die Galopp-Compliance-Richtlinie §17b.
          </div>
        </form>
      </main>
    </div>
  );
}
